import React, { useState } from "react";
import { toast } from "react-toastify";
import { userDisplayLabel } from "./AssignedUserSelect";
import ConfirmModal from "./ConfirmModal";
import WaitingRoom from "../WaitingRoom";
import {
	batchLeadRows,
	IrisLeadLink,
	optionLabelById,
	StatusChip,
	toastApiError,
	useBatchPolling,
	useMountedRef,
} from "./common";
import "./Import.css";

export default function ExecuteStep({
	api,
	fileName,
	preview,
	buildPayload,
	defaults,
	association,
	assignedUserId,
	options,
	groupBy,
	mappedCount,
	strictGrouping,
	batchId,
	onBatchStarted,
	onViewHistory,
	onRestart,
	onBack,
}) {
	const mountedRef = useMountedRef();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [starting, setStarting] = useState(false);
	// Gate for the waiting room's result view. `batchId` only ever goes
	// null → set once per mount (ImportWizard remounts this step on
	// restart), so a plain useState is enough — no reset effect needed.
	const [continued, setContinued] = useState(false);

	const { batch, error: pollError } = useBatchPolling(api, batchId, {
		enabled: batchId !== null && batchId !== undefined,
	});

	const totalLeads = preview?.groups?.length ?? 0;
	const totalContacts = preview?.totalContacts ?? 0;
	const assignedUser =
		assignedUserId === null || assignedUserId === undefined
			? null
			: (options?.users || []).find(
					(u) => String(u.id) === String(assignedUserId)
			  ) || null;
	const assignedUserLabel = assignedUser
		? userDisplayLabel(assignedUser)
		: assignedUserId !== null && assignedUserId !== undefined
		? `Iris user ${assignedUserId}`
		: "";

	const startImport = async () => {
		setStarting(true);
		try {
			const res = await api.execute({ ...buildPayload(), confirm: true });
			const id = res?.batch_id ?? res?.id;
			if (id === null || id === undefined) {
				throw new Error("execute response is missing batch_id");
			}
			if (!mountedRef.current) return;
			setConfirmOpen(false);
			onBatchStarted(id);
			toast.success(`🚀 Import started (batch #${id}).`);
		} catch (err) {
			toastApiError(err, "Failed to start import");
		} finally {
			if (mountedRef.current) setStarting(false);
		}
	};

	// ---------- Pre-start: confirmation summary ----------
	if (batchId === null || batchId === undefined) {
		return (
			<div>
				<h2>🚀 Execute Import</h2>
				<p style={{ marginBottom: 12 }}>
					Review the summary below. Executing writes leads into Iris CRM — it
					can be reverted later from Import History.
				</p>

				<div className="importTableWrap" style={{ maxWidth: 640 }}>
					<table className="importTable">
						<tbody>
							<tr>
								<td style={{ fontWeight: 600 }}>File</td>
								<td>{fileName || "—"}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Leads to create</td>
								<td>{totalLeads}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Contact rows</td>
								<td>{totalContacts}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Mapped columns</td>
								<td>{mappedCount}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Grouped by</td>
								<td>{groupBy.join(", ") || "—"}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Company matching</td>
								<td>
									{strictGrouping
										? "Strict (exact text match)"
										: "Normalized (ignores LLC/Inc, punctuation, case)"}
								</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Association</td>
								<td>{association || "—"}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Assigned to</td>
								<td>{assignedUserLabel || "None (unassigned)"}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Campaign</td>
								<td>{optionLabelById(options?.campaigns, defaults.campaign)}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Status</td>
								<td>{optionLabelById(options?.statuses, defaults.status)}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Source</td>
								<td>{optionLabelById(options?.sources, defaults.source)}</td>
							</tr>
							<tr>
								<td style={{ fontWeight: 600 }}>Group</td>
								<td>{optionLabelById(options?.groups, defaults.group)}</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div className="wizardNav">
					<button
						type="button"
						className="importBtn secondary"
						onClick={onBack}>
						← Back
					</button>
					<button
						type="button"
						className="importBtn danger"
						onClick={() => setConfirmOpen(true)}>
						Start Import…
					</button>
				</div>

				<ConfirmModal
					open={confirmOpen}
					title="Start import into Iris CRM?"
					confirmLabel="Start Import"
					busy={starting}
					onConfirm={startImport}
					onCancel={() => setConfirmOpen(false)}>
					<p>This will write to the production CRM:</p>
					<ul>
						<li>
							<b>{totalLeads}</b> new lead{totalLeads === 1 ? "" : "s"} will be
							created
						</li>
						<li>
							<b>{totalContacts}</b> contact record row
							{totalContacts === 1 ? "" : "s"} will be added
						</li>
					</ul>
					<p>
						The batch is recorded in the import ledger and can be reverted from
						Import History.
					</p>
				</ConfirmModal>
			</div>
		);
	}

	// ---------- Post-start: progress / terminal state ----------
	const status = batch?.status;
	const total = batch?.total_leads || totalLeads;
	const done = (batch?.imported || 0) + (batch?.failed || 0);
	const failedRows = batchLeadRows(batch).filter((r) => r.status === "failed");

	const isTerminal =
		status === "completed" ||
		status === "failed" ||
		status === "reverted" ||
		status === "partially_reverted";

	const doneMessage = isTerminal
		? status === "completed"
			? `Done — ${batch.imported ?? 0} imported, ${batch.failed ?? 0} failed.`
			: status === "failed"
			? `Import failed after ${batch.imported ?? 0} lead${
					(batch.imported ?? 0) === 1 ? "" : "s"
			  } — see per-lead errors below.`
			: `Batch ${String(status).replace(/_/g, " ")}.`
		: undefined;

	return (
		<div>
			<h2>🚀 Import Progress — batch #{batchId}</h2>

			{!batch && !pollError && (
				<p className="importMuted">🔄 Waiting for batch status…</p>
			)}
			{!batch && pollError && (
				<p className="importError">
					Could not fetch batch status yet — retrying…
				</p>
			)}

			{batch && (
				<>
					<div className="batchStatLine">
						<span>
							Status: <StatusChip status={status} />
						</span>
						<span>
							Imported: <b>{batch.imported ?? 0}</b>
						</span>
						<span>
							Failed: <b>{batch.failed ?? 0}</b>
						</span>
						<span>
							Total leads: <b>{total}</b>
						</span>
					</div>

					{(!isTerminal || !continued) && (
						<WaitingRoom
							active={!isTerminal}
							done={isTerminal}
							title="Importing into Iris CRM…"
							progress={{ value: done, max: total }}
							doneMessage={doneMessage}
							onContinue={() => setContinued(true)}
						/>
					)}

					{!isTerminal && (
						<p className="importMuted" style={{ fontSize: 13 }}>
							The import runs on the server — you can leave this page and check
							Import History later.
						</p>
					)}

					{isTerminal && continued && status === "completed" && (
						<div className="resultBanner success">
							✅ Import completed: <b>{batch.imported ?? 0}</b> imported,{" "}
							<b>{batch.failed ?? 0}</b> failed.
						</div>
					)}
					{isTerminal && continued && status === "failed" && (
						<div className="resultBanner error">
							❌ Import failed after <b>{batch.imported ?? 0}</b> lead
							{(batch.imported ?? 0) === 1 ? "" : "s"} — see per-lead errors
							below and Import History for details.
						</div>
					)}

					{isTerminal && continued && failedRows.length > 0 && (
						<>
							<h3>Per-lead errors ({failedRows.length})</h3>
							<div className="importTableWrap">
								<table className="importTable">
									<thead>
										<tr>
											<th>Lead (group key)</th>
											<th>Iris lead id</th>
											<th>Error</th>
										</tr>
									</thead>
									<tbody>
										{failedRows.map((r, i) => (
											<tr key={r.id ?? i}>
												<td>{r.group_key ?? "—"}</td>
												<td>
													<IrisLeadLink leadId={r.iris_lead_id} />
												</td>
												<td className="importError">{r.error || "unknown"}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</>
					)}
				</>
			)}

			{isTerminal && continued && (
				<div className="wizardNav">
					<button
						type="button"
						className="importBtn secondary"
						onClick={() => onViewHistory(batchId)}>
						View in Import History
					</button>
					<button type="button" className="importBtn" onClick={onRestart}>
						Start another import
					</button>
				</div>
			)}
		</div>
	);
}
